import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/var/www/html/src/components/Layout/Tutorials.tsx";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1>{`Setting up your local development environment with MessageBird`}</h1>
    <h3>{`⏱ 10 min read`}</h3>
    <p>{`In this MessageBird Developer Tutorial you’ll first learn how to get started with MessageBird by setting up first your local development environment.`}</p>
    <p>{`MessageBird provides an SDK (Software Development Kit) for PHP. This helper library facilitates interactions between your PHP application's code and the MessageBird APIs, so you do not have to craft raw REST API requests.`}</p>
    <h3>{`Install PHP`}</h3>
    <p>{`Make sure that you have at least PHP version 5.3 installed on your computer, as this is the minimum version of PHP that the SDK requires; we recommended you to always use the latest version of PHP.`}</p>
    <p>{`Open a terminal and run the following command:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`php -v
`}</code></pre>
    <p>{`Your terminal should reply with something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`PHP 7.1.16 (cli) (built: Mar 31 2018 02:59:59) ( NTS )
Copyright (c) 1997-2018 The PHP Group
Zend Engine v3.1.0, Copyright (c) 1998-2018 Zend Technologies
`}</code></pre>
    <p>{`If this message indicates an older version or you do not get any PHP version at all and something like `}<inlineCode parentName="p">{`php: command not found`}</inlineCode>{` instead you need to install or upgrade PHP. We’ll explain you how.`}</p>
    <h4>{`Mac`}</h4>
    <p>{`PHP is available on every Mac by default. If you are not happy with the version of PHP that comes with your Mac you can install PHP through Homebrew:`}</p>
    <ol>
      <li parentName="ol">{`If you have not used Homebrew before, `}<a parentName="li" {...{
          "href": "https://brew.sh/"
        }}>{`download it following the instructions on its homepage.`}</a></li>
      <li parentName="ol">{`Type `}<inlineCode parentName="li">{`brew install php`}</inlineCode>{` in your terminal.`}</li>
    </ol>
    <h4>{`Linux`}</h4>
    <p>{`PHP is typically available through your distribution's package manager, such as `}<inlineCode parentName="p">{`yum`}</inlineCode>{` for RedHat/CentOS based Linux distributions or `}<inlineCode parentName="p">{`apt-get`}</inlineCode>{` for Debian/Ubuntu. Alternatively, you can `}<a parentName="p" {...{
        "href": "http://php.net/manual/en/install.unix.php"
      }}>{`compile PHP from source following the instructions on php.net`}</a>{`.`}</p>
    <h4>{`Windows`}</h4>
    <p>{`Windows users have a several of options for installing PHP:`}</p>
    <ul>
      <li parentName="ul">{`Use a third-party bundle that installs PHP along with a web server and database, for example `}<a parentName="li" {...{
          "href": "https://www.apachefriends.org/"
        }}>{`XAMPP`}</a>{`. That is the preferred option for less experienced developers.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://windows.php.net/download/"
        }}>{`Download a binary release from the PHP Windows Page`}</a>{`, extract the archive and follow the instructions in `}<inlineCode parentName="li">{`install.txt`}</inlineCode>{` to configure it.`}</li>
      <li parentName="ul">{`Use the `}<a parentName="li" {...{
          "href": "https://www.iis.net/downloads/microsoft/web-platform-installer"
        }}>{`Web Platform Installer from Microsoft`}</a>{`. This option only makes sense if you use IIS.`}</li>
    </ul>
    <h3>{`Install Composer`}</h3>
    <p>{`Composer is PHP's package manager. While it's not a part of the PHP installation itself, it’s a de-facto standard that virtually every modern PHP library uses. You may be able to get the MessageBird SDK running without Composer, but we won't support it. Composer can be installed globally or locally for a single project. We recommend global installation so you can use it in multiple projects.`}</p>
    <p>{`To confirm Composer is installed, open a terminal and run the following command:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`composer -V
`}</code></pre>
    <p>{`Your terminal should reply with something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`Composer version 1.7.1 2018-08-07 09:39:23
`}</code></pre>
    <p>{`If this message indicates an ancient version or you do not get any Composer version at all and something like `}<inlineCode parentName="p">{`composer: command not found`}</inlineCode>{` instead, you need to install or upgrade Composer.`}</p>
    <p>{`Don’t worry! Upgrading is easy, just type `}<inlineCode parentName="p">{`composer self-update`}</inlineCode>{`.`}</p>
    <p>{`For a new installation simply follow the instructions on getcomposer.org for `}<a parentName="p" {...{
        "href": "https://getcomposer.org/doc/00-intro.md#installation-linux-unix-osx"
      }}>{`Mac/Linux`}</a>{` or `}<a parentName="p" {...{
        "href": "https://getcomposer.org/doc/00-intro.md#installation-windows"
      }}>{`Windows`}</a>{`.`}</p>
    <h3>{`Install the MessageBird SDK`}</h3>
    <p>{`As we mentioned, you should use Composer to install the MessageBird SDK for PHP. The SDK is open source and you can `}<a parentName="p" {...{
        "href": "https://github.com/messagebird/php-rest-api"
      }}>{`browse the code in this GitHub repository`}</a>{`; it’s also `}<a parentName="p" {...{
        "href": "https://packagist.org/packages/messagebird/php-rest-api"
      }}>{`listed on Packagist`}</a>{` so that Composer knows where to find it. SDKs and other libraries are always installed for each project as a dependency and stored in a project-specific directory called `}<inlineCode parentName="p">{`vendor`}</inlineCode>{` that Composer manages.`}</p>
    <p>{`There are two ways to add a package with Composer:`}</p>
    <h4>{`Using the Require Command`}</h4>
    <p>{`Create a new project directory or open an existing project directory in your terminal. Then, type the following command:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`composer require messagebird/php-rest-api
`}</code></pre>
    <p>{`This command installs the latest version of the SDK and automatically creates or updates the files `}<inlineCode parentName="p">{`composer.json`}</inlineCode>{` and `}<inlineCode parentName="p">{`composer.lock`}</inlineCode>{`. 😎`}</p>
    <h4>{`Using composer.json`}</h4>
    <p>{`Create a new project directory or open an existing project directory. In this directory, create a file called `}<inlineCode parentName="p">{`composer.json`}</inlineCode>{`; if you already have this file, you can add the SDK to it; if you only have the SDK in it, the file should look like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
   "require": {
       "messagebird/php-rest-api": "^1.10"
   }
}
`}</code></pre>
    <p>{`Keep in mind that you have to specify the version of the SDK in the file. You can `}<a parentName="p" {...{
        "href": "https://packagist.org/packages/messagebird/php-rest-api"
      }}>{`see the available SDK versions on Packagist`}</a>{` and learn more about different ways to specify versions, for example with ranges, `}<a parentName="p" {...{
        "href": "https://getcomposer.org/doc/articles/versions.md"
      }}>{`in the Composer documentation`}</a>{`.`}</p>
    <p>{`After saving the file, open a terminal in the directory into which you've stored it and type this command:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`composer install
`}</code></pre>
    <p><strong parentName="p">{`Nice work!`}</strong>{` 🎉`}</p>
    <p>{`Your PHP development environment and project directory are ready now!`}</p>
    <h3>{`Start building!`}</h3>
    <p>{`Your PHP development environment and project directory are ready now. Let's head over to the next `}<strong parentName="p">{`MessageBird Developer Tutorial`}</strong>{` and `}<a parentName="p" {...{
        "href": "/tutorials/send-sms-node"
      }}>{`learn how to send your first SMS using PHP`}</a>{`.`}</p>
    <p>{`Want to start building your solution but not quite sure how to get started? Feel free to let us know at `}<a parentName="p" {...{
        "href": "mailto:support@messagebird.com"
      }}>{`support@messagebird.com`}</a>{`; we'd love to help!`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      